// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { PowerFlowLayout } from '../../../utils/powerFlowLayout';
import type { PowerFlowLiveState } from '../../../utils/powerFlowLive';
import {
  INPUT_PHASE_CURRENT_KINDS,
  INPUT_VOLTAGE_KINDS,
  LOAD_POWER_KINDS,
  POWER_FACTOR_KINDS,
} from '../../../utils/powerFlowReadings';
import { SHEET_CHROME, type SheetBand, type SheetColumn } from '../../../utils/powerFlowSheets';
import { STATUS_LABELS } from '../../../utils/statusPresentation';
import { StatusMark } from './primitives/StatusMark';

export interface SldSheetChromeProps {
  columns: SheetColumn[];
  bands: SheetBand[];
  layout: PowerFlowLayout;
  statusOf: Map<string, StatusToken>;
  /**
   * Drawn node id → worst status among the loads folded into it and not drawn.
   * A column's status has to roll those up too, or a faulted rack behind an
   * RPP's `14 IT Racks` would leave its block pill reading healthy.
   */
  foldedStatusOf?: ReadonlyMap<string, StatusToken>;
  powerLive: PowerFlowLiveState;
  /** Short name for a stub's node, for its accessible text. */
  labelOf: (_id: string) => string;
  /**
   * The block titles are an inference (`Block A · from labels`). With the
   * inferred tier off a column is titled by the board it meters, which the
   * twin published, and the frame stays because it is layout, not a claim.
   */
  showInferred?: boolean;
}

const STUB_LENGTH = 22;

interface MeterTile {
  label: string;
  /** As drawn on the card: `480 V`, `1,846 A`, `1,488 kW`, `PF 0.97`, or `PF —`. */
  text: string;
  stale: boolean;
  /** The same figure as a spoken phrase: `1,846 A`, `PF 0.97`. Absent when unpublished. */
  spoken?: string;
}

/** `480 V` · `1,846 A` · `1,488 kW` · `PF 0.97`, an em-dash where unpublished. */
const meterTiles = (boardId: string | undefined, live: PowerFlowLiveState): MeterTile[] => {
  const volts = boardId ? live.voltsOf.get(boardId) : undefined;
  const amps = boardId ? live.ampsOf.get(boardId) : undefined;
  const kw = boardId ? live.powerKwOf.get(boardId) : undefined;
  const pf = boardId ? live.powerFactorOf.get(boardId) : undefined;
  // Stale per figure, not per board: a frozen PF must not grey a live voltage.
  const staleKinds = new Set(boardId ? (live.staleKindsOf.get(boardId) ?? []) : []);
  const staleAmong = (kinds: readonly string[]) => kinds.some(kind => staleKinds.has(kind));
  const num = (n: number, digits: number) =>
    n.toLocaleString('en-US', { maximumFractionDigits: digits });
  const tile = (
    label: string,
    n: number | undefined,
    digits: number,
    stale: boolean,
    textOf: (_figure: string) => string
  ): MeterTile =>
    n === undefined
      ? { label, text: textOf('—'), stale }
      : { label, text: textOf(num(n, digits)), stale, spoken: textOf(num(n, digits)) };
  return [
    tile('Bus V', volts, 0, staleAmong(INPUT_VOLTAGE_KINDS), figure => `${figure} V`),
    tile('Bus A', amps, 0, staleAmong(INPUT_PHASE_CURRENT_KINDS), figure => `${figure} A`),
    tile('kW', kw, 0, staleAmong(LOAD_POWER_KINDS), figure => `${figure} kW`),
    tile('PF', pf, 2, staleAmong(POWER_FACTOR_KINDS), figure => `PF ${figure}`),
  ];
};

/**
 * The card, as a sentence: `480 V, 1,846 A, 1,488 kW, PF 0.97`. The card
 * itself is `aria-hidden` — read as fragments it is four numbers — so this is
 * where a screen reader gets the board's figures.
 */
const meterSummary = (tiles: MeterTile[]): string => {
  const spoken = tiles.map(tile => tile.spoken).filter((s): s is string => Boolean(s));
  if (spoken.length === 0) return 'no metering published';
  // A figure is only ever drawn from a fresh reading, so a stale tile is one
  // showing an em-dash; name it rather than greying the fresh ones beside it.
  const stale = tiles.filter(tile => tile.stale).map(tile => tile.label);
  return `${spoken.join(', ')}${stale.length > 0 ? ` (${stale.join(', ')} stale)` : ''}`;
};

const worstOf = (
  ids: readonly string[],
  statusOf: Map<string, StatusToken>,
  foldedStatusOf?: ReadonlyMap<string, StatusToken>
): StatusToken => {
  const SEVERITY: Record<StatusToken, number> = { idle: 0, ok: 1, warn: 2, fault: 3 };
  let worst: StatusToken = 'idle';
  for (const id of ids) {
    for (const status of [statusOf.get(id) ?? 'idle', foldedStatusOf?.get(id) ?? 'idle']) {
      if (status === 'fault') return 'fault';
      if (SEVERITY[status] > SEVERITY[worst]) worst = status;
    }
  }
  return worst;
};

/**
 * The frame around each block column: header with the board's readings and
 * the status pill, off-sheet stubs and the continuation lines.
 *
 * This is the part of the drawing that makes it read as the site's own
 * Ignition sheets. Each block is a bounded panel titled `Block A`, with the
 * block's board readings — `480 V · 1,846 A · 1,488 kW · PF 0.97` — as one
 * line of text in the header beside the status pill, out of the drawing's way. The two bands — input switchgear above, output switchgear,
 * PDUs and RPPs below — are titled down the left edge, and the MV loop enters
 * each block as a `From FEEDER-1 · MV loop` stub above its transformer. Every
 * other connection is a drawn conductor, including the ones between blocks.
 *
 * Drawn behind the conductors and the boxes, non-interactive. The stubs and
 * the meter card are `aria-hidden` — the stubs restate connectivity that each
 * asset's accessible name already carries, and the card reads as fragments —
 * and a visually hidden sentence per column speaks the title, the status and
 * the card's figures instead.
 */
const SldSheetChromeComponent = ({
  columns,
  bands,
  layout,
  statusOf,
  foldedStatusOf,
  powerLive,
  labelOf,
  showInferred = true,
}: SldSheetChromeProps) => {
  if (columns.length === 0) return null;
  const chromeTop = SHEET_CHROME.headerHeight + SHEET_CHROME.continuationHeight;

  return (
    <div className="sld-sheet-chrome">
      {bands.map(band => (
        <span
          key={band.id}
          className="sld-band__title"
          style={{ top: band.y, height: band.h, width: SHEET_CHROME.bandTitleWidth }}
          data-sld-band={band.id}
        >
          {band.title}
        </span>
      ))}
      {columns.map(column => {
        const status = worstOf(column.assetIds, statusOf, foldedStatusOf);
        const tiles = meterTiles(column.meterBoardId, powerLive);
        const topStubs = column.stubs;
        // `Block A` is inferred from labels; the board's own name is not.
        const title =
          showInferred || !column.id.startsWith('block:')
            ? column.title
            : column.meterBoardId
              ? labelOf(column.meterBoardId)
              : '';
        return (
          <div
            key={`${column.band}:${column.id}`}
            className={`sld-column sld-column--${status}`}
            style={{ left: column.x, top: column.y, width: column.w, height: column.h }}
            data-sld-column={column.id}
            data-sld-band={column.band}
          >
            <div className="sld-column__header" style={{ height: SHEET_CHROME.headerHeight }}>
              <span className="sld-column__title">{title}</span>
              <span className="sld-column__header-readings">
                <span className="sld-column__meter-card" aria-hidden="true" data-sld-meter-card>
                  {tiles.map((tile, index) => (
                    <React.Fragment key={tile.label}>
                      {index > 0 && <span className="sld-column__meter-sep">·</span>}
                      <span className={`sld-column__meter-value${tile.stale ? ' is-stale' : ''}`}>
                        {tile.text}
                      </span>
                    </React.Fragment>
                  ))}
                </span>
                <span className="sld-column__pill" data-sld-column-status={status}>
                  <StatusMark status={status} />
                  {STATUS_LABELS[status]}
                </span>
              </span>
            </div>
            {column.continuation && (
              <span
                className="sld-column__continuation"
                style={{ top: chromeTop - SHEET_CHROME.continuationHeight + 6 }}
                data-sld-continuation
              >
                {column.continuation}
              </span>
            )}
            <svg
              className="sld-column__stubs"
              width={column.w}
              height={column.h}
              viewBox={`0 0 ${column.w} ${column.h}`}
              aria-hidden="true"
              focusable="false"
            >
              {topStubs.map(stub => {
                const node = layout.nodesById.get(stub.nodeId);
                if (!node) return null;
                const x = node.x + node.w / 2 - column.x;
                const y1 = node.y - column.y - (node.shape === 'bus' ? 20 : 0);
                const y0 = y1 - STUB_LENGTH;
                return (
                  <g key={`${stub.nodeId}:${stub.label}`} className="sld-stub sld-stub--top">
                    <path d={`M ${x} ${y0} L ${x} ${y1}`} />
                    <path d={`M ${x - 4} ${y1 - 6} L ${x} ${y1} L ${x + 4} ${y1 - 6}`} />
                    <text x={x + 6} y={y0 + 4} className="sld-stub__label">
                      {stub.label}
                    </text>
                  </g>
                );
              })}
            </svg>
            <span className="sld-sr-only" data-sld-column-summary>
              {`${title}, ${STATUS_LABELS[status]}${
                column.continuation ? `, ${column.continuation}` : ''
              }${
                topStubs.length > 0
                  ? `, fed at ${topStubs.map(s => labelOf(s.nodeId)).join(', ')}`
                  : ''
              }. Metering: ${meterSummary(tiles)}.`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const SldSheetChrome = React.memo(SldSheetChromeComponent);
